import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { Observable, finalize } from 'rxjs';

import { Rolle } from '../../models/rolle.model';

export interface RolleDialogResult {
  name: string;
  istAdmin: boolean;
  module: string[];
}

export interface ModulOption {
  label: string;
  value: string;
}

export interface RolleDialogData {
  mode: 'create' | 'edit';
  rolle?: Rolle;
  moduleOptions: ModulOption[];
  submitRequest: (result: RolleDialogResult) => Observable<unknown>;
}

@Component({
  selector: 'app-rollen-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatSlideToggleModule,
    MatSnackBarModule,
  ],
  templateUrl: './rollen-dialog.component.html',
  styleUrl: './rollen-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RollenDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialogRef = inject(
    MatDialogRef<RollenDialogComponent, boolean>
  );

  protected readonly data = inject<RolleDialogData>(MAT_DIALOG_DATA);
  protected readonly isSaving = signal(false);

  protected readonly moduleOptions = this.data.moduleOptions;
  private readonly allModuleValues = this.moduleOptions.map((module) => module.value);

  protected readonly form = this.fb.nonNullable.group({
    name: [
      this.data.rolle?.name ?? '',
      [Validators.required, Validators.maxLength(80)],
    ],
    istAdminRolle: [this.data.rolle?.istAdminRolle ?? false],
    module: [
      this.data.rolle?.istAdminRolle
        ? []
        : this.data.rolle?.module ?? [],
    ],
  });

  protected get dialogTitle(): string {
    return this.data.mode === 'edit' ? 'Rolle bearbeiten' : 'Neue Rolle erstellen';
  }

  protected close(): void {
    if (this.isSaving()) {
      return;
    }

    this.dialogRef.close(false);
  }

  protected toggleModule(moduleValue: string): void {
    if (this.isSaving()) {
      return;
    }

    const selectedModules = this.form.controls.module.value;
    const isSelected = selectedModules.includes(moduleValue);

    const updatedModules = isSelected
      ? selectedModules.filter((module) => module !== moduleValue)
      : [...selectedModules, moduleValue];

    this.form.controls.module.setValue(updatedModules);
  }

  protected isModuleSelected(moduleValue: string): boolean {
    return this.form.controls.module.value.includes(moduleValue);
  }

  protected save(): void {
    if (this.isSaving()) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const isAdmin = this.form.controls.istAdminRolle.value;

    const result: RolleDialogResult = {
      name: this.form.controls.name.value.trim(),
      istAdmin: isAdmin,
      module: isAdmin ? this.allModuleValues : this.form.controls.module.value,
    };

    this.isSaving.set(true);

    this.data.submitRequest(result)
      .pipe(finalize(() => this.isSaving.set(false)))
      .subscribe({
        next: () => {
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackBar.open(
            'Speichern fehlgeschlagen. Bitte erneut versuchen.',
            '×',
            {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            }
          );
        },
      });
  }
}